import { useEffect, useMemo, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import './painel-baixa.css'

/**
 * Dar baixa aceitando a realidade do balcão.
 *
 * A API já aceita `pago_centavos` (recebido menor que o devido vira
 * PARCIAL) e `liquido_centavos` (o que sobra depois da taxa da
 * maquininha) desde sempre — e a tela só mandava `meio`, cheia. Três
 * coisas ficavam de fora, e cada uma é dinheiro:
 *
 *  · **Parcial.** O paciente paga R$ 100 de R$ 150. Sem isto, ou some
 *    a dívida inteira ou nada entra.
 *  · **Líquido do cartão.** R$ 150 na maquininha caem R$ 145,50. A taxa
 *    existe na coluna, é aceita pelo POST, e ninguém digitava — são
 *    ~R$ 13,5 mil/ano numa clínica de R$ 300 mil.
 *  · **Troco.** Recebeu R$ 200 em espécie por uma conta de R$ 150; o
 *    troco é R$ 50. O que quita a conta é R$ 150, não R$ 200.
 */

const MEIOS = [
  { id: 'PIX',            rotulo: 'Pix' },
  { id: 'DINHEIRO',       rotulo: 'Dinheiro' },
  { id: 'CARTAO_CREDITO', rotulo: 'Crédito' },
  { id: 'CARTAO_DEBITO',  rotulo: 'Débito' },
  { id: 'BOLETO',         rotulo: 'Boleto' },
  { id: 'CORTESIA',       rotulo: 'Cortesia' },
] as const

const brl = (c: number) =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const reais = (txt: string) =>
  Math.round(Number(txt.replace(/\./g, '').replace(',', '.') || 0) * 100)
const paraReais = (c: number) => (c / 100).toFixed(2).replace('.', ',')

export function PainelBaixa({ bruto, jaPago, aoConfirmar, aoFechar }: {
  bruto: number
  jaPago: number
  aoConfirmar(corpo: Record<string, unknown>): Promise<void>
  aoFechar(): void
}) {
  const devido = Math.max(0, bruto - jaPago)
  const [meio, setMeio] = useState('PIX')
  const [valor, setValor] = useState(paraReais(devido))
  const [especie, setEspecie] = useState('')
  const [liquido, setLiquido] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const ehCartao = meio === 'CARTAO_CREDITO' || meio === 'CARTAO_DEBITO'
  const ehDinheiro = meio === 'DINHEIRO'

  const pago = reais(valor)
  const parcial = pago > 0 && pago < devido
  const troco = ehDinheiro ? Math.max(0, reais(especie) - pago) : 0
  const taxa = ehCartao && liquido ? pago - reais(liquido) : 0

  useEffect(() => {
    const noEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    document.addEventListener('keydown', noEscape)
    return () => document.removeEventListener('keydown', noEscape)
  }, [aoFechar])

  async function confirmar(e: React.FormEvent) {
    e.preventDefault()
    if (pago <= 0) { setErro('Informe o valor recebido.'); return }
    setSalvando(true); setErro(null)
    try {
      await aoConfirmar({
        meio,
        // Só manda `pago_centavos` quando NÃO é o valor cheio — mandar
        // igual ao devido é ruído, e a rota já assume cheio sem ele.
        ...(pago !== devido ? { pago_centavos: pago } : {}),
        ...(ehCartao && liquido ? { liquido_centavos: reais(liquido) } : {}),
      })
    } catch (err) {
      setErro(err instanceof ErroApi
        ? (ehDinheiro && (err.codigo === 'INVALIDO' || err.status === 409)
            ? 'Para receber em dinheiro, abra o caixa primeiro (O dinheiro › Caixa).'
            : err.message)
        : 'Não foi possível dar baixa.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel pbx" role="dialog" aria-modal="true" aria-label="Dar baixa">
        <header className="painel__topo">
          <h2>Receber</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>Fechar</button>
        </header>

        <p className="ajuda">
          Devido: <strong>{brl(devido)}</strong>
          {jaPago > 0 && ` · já pago ${brl(jaPago)} de ${brl(bruto)}`}
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <form className="pbx__form" onSubmit={confirmar}>
          <div className="pbx__meios" role="group" aria-label="Forma de pagamento">
            {MEIOS.map((m) => (
              <button key={m.id} type="button"
                      className={`pbx__meio${meio === m.id ? ' is-ativo' : ''}`}
                      aria-pressed={meio === m.id}
                      onClick={() => setMeio(m.id)}>{m.rotulo}</button>
            ))}
          </div>

          <div className="campo">
            <label htmlFor="pbx-valor">Valor recebido</label>
            <input id="pbx-valor" className="entrada" inputMode="decimal" autoFocus
                   value={valor} onChange={(e) => setValor(e.target.value)} />
            {parcial && (
              <span className="ajuda">
                Parcial — fica <strong>{brl(devido - pago)}</strong> em aberto.
              </span>
            )}
          </div>

          {/* Dinheiro: quanto veio na mão, para calcular o troco. */}
          {ehDinheiro && (
            <div className="campo">
              <label htmlFor="pbx-esp">Recebido em espécie</label>
              <input id="pbx-esp" className="entrada" inputMode="decimal"
                     placeholder={paraReais(pago)}
                     value={especie} onChange={(e) => setEspecie(e.target.value)} />
              {troco > 0 && (
                <span className="ajuda">Troco: <strong>{brl(troco)}</strong></span>
              )}
            </div>
          )}

          {/* Cartão: o que efetivamente cai, depois da taxa. */}
          {ehCartao && (
            <div className="campo">
              <label htmlFor="pbx-liq">Líquido (o que cai na conta)</label>
              <input id="pbx-liq" className="entrada" inputMode="decimal"
                     placeholder={paraReais(pago)}
                     value={liquido} onChange={(e) => setLiquido(e.target.value)} />
              {taxa > 0 && (
                <span className="ajuda">Taxa da maquininha: <strong>{brl(taxa)}</strong></span>
              )}
            </div>
          )}

          <button className="botao pbx__ok" type="submit" disabled={salvando}>
            {salvando ? 'Recebendo…' : `Receber ${brl(pago > 0 ? pago : devido)}`}
          </button>
        </form>
      </aside>
    </>
  )
}
